export type OsVersionRange = { min?: string; max?: string };
export type OsVersions = Partial<Record<'IOS' | 'ANDROID', OsVersionRange>>;

const versionPattern = /^\d{1,2}(?:\.\d{1,2}){0,2}$/;
export function compareVersions(a: string, b: string): number {
  const left = a.split('.').map(Number), right = b.split('.').map(Number);
  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    const diff = (left[i] ?? 0) - (right[i] ?? 0);
    if (diff) return diff;
  }
  return 0;
}

export function validateOsVersions(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value !== 'object' || Array.isArray(value)) return 'Invalid OS versions';
  for (const [os, range] of Object.entries(value)) {
    if (!['IOS', 'ANDROID'].includes(os) || !range || typeof range !== 'object' || Array.isArray(range)) return 'Invalid OS version range';
    const { min, max } = range as OsVersionRange;
    for (const version of [min, max]) {
      if (version != null && version !== '' && (typeof version !== 'string' || !versionPattern.test(version) || Number(version.split('.')[0]) < 1)) return 'Use an OS version such as 14 or 14.2';
    }
    if (max && !min) return 'Select a minimum OS version before setting a maximum';
    if (min && max && compareVersions(min, max) > 0) return 'Minimum OS version must not exceed maximum';
  }
  return null;
}

/** Meta encodes the version range inside targeting.user_os, not separate fields. */
export function buildMetaUserOs(systems: string[] | undefined, versions?: OsVersions): string[] | undefined {
  const error = validateOsVersions(versions);
  if (error) throw new Error(error);
  return systems?.map((os) => {
    const prefix = os === 'IOS' ? 'iOS' : 'Android';
    const range = versions?.[os as keyof OsVersions];
    const min = range?.min;
    const max = range?.max;
    const format = (version: string) => version.includes('.') ? version : `${version}.0`;
    return min ? `${prefix}_ver_${format(min)}_${max ? `to_${format(max)}` : 'and_above'}` : prefix;
  });
}
